import { Restaurant } from "api-types";
import { StyledButton } from "igfd-ui";

import React, { useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material/";
import AppBar from "@mui/material/AppBar";
import Container from "@mui/material/Container";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Toolbar from "@mui/material/Toolbar";
import { styled } from "@mui/material/styles";

import IgfdBlackLogo from "@dashboard/assets/images/logo/logo-name-black.png";
import { CloseIcon, MenuIcon } from "@dashboard/components/common/icons";
import { menuSurfaceSx } from "@dashboard/components/common/menuSurface";
import { SelectedRestaurant } from "@dashboard/components/header/selectedRestaurant/SelectedRestaurant";
import { useDisclosure } from "@dashboard/hooks/useDisclosure";
import { ReduxState } from "@dashboard/types/reduxState";

import { ManagerAvatar } from "../manager-avatar/ManagerAvatar";

interface HeaderComponentProps {
  isLargeScreen: boolean;
  isLeftDrawerOpen: boolean;
  toggleLeftDrawer: () => void;

  /**
   * Display name of the manager.
   */
  managerDisplayName?: string;

  /**
   * Photo URL of the manager.
   */
  managerPhotoUrl?: string;

  // Null when no restaurants exists.
  selectedRestaurant: Restaurant | null;

  /**
   * Navigate callback to react-router-dom's history.push
   */
  navigate: (to: string) => void;
  logout: () => void;
}

/**
 * Presentation component for showing the header of the dashboard.
 */
export const HeaderComponent = ({
  isLargeScreen,
  isLeftDrawerOpen,
  toggleLeftDrawer,
  managerDisplayName,
  managerPhotoUrl,
  selectedRestaurant,
  navigate,
  logout,
}: HeaderComponentProps) => {
  const managerInitials = (managerDisplayName || "G").charAt(0).toUpperCase(); // G stands for Guest
  return (
    <AppBar
      sx={({ palette }) => ({
        backgroundColor: palette.grey[50],
        color: palette.grey[900],
        boxShadow: "none",
        borderBottom: `1px solid ${palette.grey.A200}`,
      })}
    >
      <Container maxWidth={false}>
        <Toolbar disableGutters>
          {isLargeScreen && <LogoComponent navigate={navigate} />}
          {selectedRestaurant && !isLargeScreen && (
            // a restaurant should exist for this to be shown
            // this controls restaurant navigation since the side bar isn't visible in mobile.
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={toggleLeftDrawer}
              sx={{
                mr: 1,
              }}
            >
              {isLeftDrawerOpen ? (
                <CloseIcon size={24} />
              ) : (
                <MenuIcon size={24} />
              )}
            </IconButton>
          )}
          <SelectableRestaurantComponent
            isLargeScreen={isLargeScreen}
            selectedRestaurant={selectedRestaurant}
            navigate={navigate}
          />
          {isLargeScreen && (
            <AccountNavComponent
              managerInitials={managerInitials}
              managerPhotoUrl={managerPhotoUrl}
              logout={logout}
              navigate={navigate}
            />
          )}
          {!isLargeScreen && (
            <MobileAccountNavComponent
              navigate={navigate}
              logout={logout}
              managerInitials={managerInitials}
              managerPhotoUrl={managerPhotoUrl}
            />
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

const LogoComponent = ({ navigate }: { navigate: (to: string) => void }) => (
  <Box
    onClick={() => navigate("/")}
    sx={{
      mr: 4,
      mb: 2,
      display: "flex",
      cursor: "pointer",
    }}
  >
    <img
      src={IgfdBlackLogo}
      style={{
        width: "107px",
        objectFit: "cover",
      }}
    />
  </Box>
);

const SelectableRestaurantComponent = ({
  isLargeScreen,
  selectedRestaurant,
  navigate,
}: {
  isLargeScreen: boolean;
  selectedRestaurant: Restaurant | null;
  navigate: (to: string) => void;
}) => {
  return (
    <Stack direction={"row"} alignItems={"center"} gap={2} sx={{ flexGrow: 1 }}>
      {!selectedRestaurant && (
        <StyledButton
          label={"Create a restaurant"}
          size={"small"}
          onClick={() => navigate("/restaurants/create")}
          sx={({ palette }) => ({
            color: palette.grey[900],
            my: 2,
            display: "flex",
          })}
        />
      )}
      {selectedRestaurant && (
        <SelectedRestaurant
          isLargeScreen={isLargeScreen}
          selectedRestaurant={selectedRestaurant}
        />
      )}
    </Stack>
  );
};

const AccountNavComponent = ({
  managerPhotoUrl,
  managerInitials,
  logout,
  navigate,
}: {
  managerPhotoUrl?: string;
  managerInitials: string;
  logout: () => void;
  navigate: (to: string) => void;
}) => {
  const userIsAdmin = useSelector(
    (state: ReduxState) => state.dashboardApp.userIsAdmin
  );
  const { isOpen: isMenuOpen, toggle: toggleMenu } = useDisclosure();
  const anchorRef = useRef<HTMLButtonElement>(null);

  return (
    <Stack direction={"row"} alignItems={"center"} gap={2} sx={{ flexGrow: 0 }}>
      <Tooltip title={userIsAdmin ? "Account menu" : "View my profile"}>
        <IconButton
          onClick={userIsAdmin ? toggleMenu : () => navigate("/account")}
          sx={{ p: 0 }}
          ref={anchorRef}
        >
          <ManagerAvatar
            managerPhotoUrl={managerPhotoUrl}
            managerInitials={managerInitials}
            isAdmin={userIsAdmin}
          />
        </IconButton>
      </Tooltip>
      {userIsAdmin && (
        <Menu
          sx={({ spacing }) => ({ mt: spacing(4), ...menuSurfaceSx })}
          anchorEl={anchorRef.current}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          open={isMenuOpen}
          onClose={toggleMenu}
        >
          <MenuItem
            onClick={() => {
              toggleMenu();
              navigate("/account");
            }}
          >
            <SmallTypography>Profile settings</SmallTypography>
          </MenuItem>
          <AdminSettingsMenuItem onClose={toggleMenu} />
        </Menu>
      )}
      <StyledButton
        label={"Log out"}
        size={"small"}
        isGreyStyle
        variant={"text"}
        onClick={logout}
      />
    </Stack>
  );
};

const MobileAccountNavComponent = ({
  navigate,
  logout,
  managerPhotoUrl,
  managerInitials,
}: {
  logout: () => void;
  navigate: (to: string) => void;
  managerPhotoUrl?: string;
  managerInitials: string;
}) => {
  const userIsAdmin = useSelector(
    (state: ReduxState) => state.dashboardApp.userIsAdmin
  );
  const { isOpen: isAccountMenuOpen, toggle: toggleAccountMenu } =
    useDisclosure();

  const anchorRef = useRef<HTMLButtonElement>(null);

  return (
    <Box sx={{ flexGrow: 0 }}>
      <IconButton onClick={toggleAccountMenu} sx={{ p: 0 }} ref={anchorRef}>
        <ManagerAvatar
          managerPhotoUrl={managerPhotoUrl}
          managerInitials={managerInitials}
          isAdmin={userIsAdmin}
        />
      </IconButton>
      <Menu
        sx={({ spacing }) => ({ mt: spacing(4), ...menuSurfaceSx })}
        id="menu-appbar"
        anchorEl={anchorRef.current}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={isAccountMenuOpen}
        onClose={toggleAccountMenu}
      >
        <MenuItem
          onClick={() => {
            toggleAccountMenu();
            navigate("/account");
          }}
        >
          <SmallTypography>Profile settings</SmallTypography>
        </MenuItem>
        {userIsAdmin && <AdminSettingsMenuItem onClose={toggleAccountMenu} />}
        <MenuItem
          onClick={() => {
            toggleAccountMenu();
            logout();
          }}
        >
          <SmallTypography>Log out</SmallTypography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

const ShortcutKey = ({ children }: { children: React.ReactNode }) => (
  <Box
    component="kbd"
    sx={({ palette }) => ({
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 18,
      height: 18,
      px: 0.5,
      fontFamily: "inherit",
      fontSize: "0.6875rem",
      lineHeight: 1,
      color: palette.text.secondary,
      backgroundColor: palette.action.hover,
      borderRadius: 0.75,
    })}
  >
    {children}
  </Box>
);

const AdminSettingsMenuItem = ({ onClose }: { onClose: () => void }) => {
  const navigate = useNavigate();
  return (
    <MenuItem
      onClick={() => {
        onClose();
        navigate("/admin");
      }}
      sx={{ justifyContent: "center", gap: 1 }}
    >
      <Typography
        variant="labelXSMedium"
        sx={({ palette }) => ({ color: palette.secondary.main })}
      >
        Admin settings
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <ShortcutKey>⌘</ShortcutKey>
        <ShortcutKey>⇧</ShortcutKey>
        <ShortcutKey>.</ShortcutKey>
      </Stack>
    </MenuItem>
  );
};

const SmallTypography = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.caption.fontSize,
  fontFamily: theme.typography.caption.fontFamily,
  textAlign: "center",
}));
